import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send, MessageSquare, CornerDownRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }) };

export default function ForumDetailPage() {
  const { forumId } = useParams();
  const [forum, setForum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postForm, setPostForm] = useState({ author_name: "", author_email: "", content: "" });
  const [replyTo, setReplyTo] = useState(null);
  const [replyForm, setReplyForm] = useState({ author_name: "", author_email: "", content: "" });

  const fetchForum = () => { axios.get(`${API}/forums/${forumId}`).then(r => setForum(r.data)).catch(() => toast.error("Forum not found")).finally(() => setLoading(false)); };
  useEffect(fetchForum, [forumId]);

  const submitPost = async () => {
    if (!postForm.author_name || !postForm.content) { toast.error("Name and message required."); return; }
    try {
      await axios.post(`${API}/forums/posts`, { ...postForm, forum_id: forumId });
      setPostForm({ author_name: postForm.author_name, author_email: postForm.author_email, content: "" });
      fetchForum();
      toast.success("Posted.");
    } catch { toast.error("Failed."); }
  };

  const submitReply = async (postId) => {
    if (!replyForm.author_name || !replyForm.content) { toast.error("Name and reply required."); return; }
    try {
      await axios.post(`${API}/forums/replies`, { ...replyForm, post_id: postId });
      setReplyForm({ author_name: replyForm.author_name, author_email: replyForm.author_email, content: "" });
      setReplyTo(null);
      fetchForum();
      toast.success("Reply posted.");
    } catch { toast.error("Failed."); }
  };

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold animate-pulse font-body">Loading...</div></div>;
  if (!forum) return null;

  return (
    <div data-testid="forum-detail-page" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/community" className="inline-flex items-center gap-2 text-white/40 hover:text-gold text-sm font-body mb-8 transition-colors"><ArrowLeft size={16} /> Back to Community</Link>

        <motion.div initial="hidden" animate="visible">
          <motion.div variants={fadeUp} custom={0} className="mb-2">
            <span className="px-2 py-0.5 text-[10px] bg-gold/10 text-gold font-body uppercase">{forum.category}</span>
          </motion.div>
          <motion.h1 variants={fadeUp} custom={1} className="font-heading text-3xl text-white mb-2" data-testid="forum-title">{forum.title}</motion.h1>
          {forum.description && <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body text-sm mb-8">{forum.description}</motion.p>}
        </motion.div>

        {/* New Post */}
        <div className="glass-card p-6 mb-10 space-y-4" data-testid="new-post-form">
          <h3 className="text-white font-body text-sm font-medium">Join the Discussion</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input data-testid="post-name" value={postForm.author_name} onChange={e => setPostForm({...postForm, author_name: e.target.value})} placeholder="Your name *" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
            <Input data-testid="post-email" type="email" value={postForm.author_email} onChange={e => setPostForm({...postForm, author_email: e.target.value})} placeholder="Email (private)" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
          </div>
          <Textarea data-testid="post-content" value={postForm.content} onChange={e => setPostForm({...postForm, content: e.target.value})} placeholder="Share your thoughts..." rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-sm resize-none" />
          <button data-testid="submit-post" onClick={submitPost} className="inline-flex items-center gap-2 bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors"><Send size={14} /> Post</button>
        </div>

        {/* Posts */}
        {(forum.posts || []).length === 0 ? (
          <div className="text-center py-12"><p className="text-white/30 font-body text-sm">No posts yet. Start the conversation above.</p></div>
        ) : (
          <motion.div initial="hidden" animate="visible" className="space-y-6">
            {forum.posts.map((post, i) => (
              <motion.div key={post.id} variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`post-${post.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-gold font-body text-sm font-medium">{post.author_name}</span>
                  <span className="text-white/20 text-[10px] font-body">{post.created_at ? new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</span>
                </div>
                <p className="text-white/70 font-body text-sm leading-relaxed mb-4">{post.content}</p>

                {/* Replies */}
                {(post.replies || []).length > 0 && (
                  <div className="ml-6 border-l border-white/10 pl-4 space-y-3 mb-4">
                    {post.replies.map((reply) => (
                      <div key={reply.id} className="py-2">
                        <div className="flex items-center gap-2 mb-1">
                          <CornerDownRight size={12} className="text-gold/50" />
                          <span className="text-gold/70 font-body text-xs font-medium">{reply.author_name}</span>
                          <span className="text-white/15 text-[9px] font-body">{reply.created_at ? new Date(reply.created_at).toLocaleDateString() : ""}</span>
                        </div>
                        <p className="text-white/50 font-body text-xs leading-relaxed ml-5">{reply.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {replyTo === post.id ? (
                  <div className="ml-6 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input value={replyForm.author_name} onChange={e => setReplyForm({...replyForm, author_name: e.target.value})} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9 rounded-sm text-xs" />
                      <Input type="email" value={replyForm.author_email} onChange={e => setReplyForm({...replyForm, author_email: e.target.value})} placeholder="Email" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9 rounded-sm text-xs" />
                    </div>
                    <Textarea value={replyForm.content} onChange={e => setReplyForm({...replyForm, content: e.target.value})} placeholder="Your reply..." rows={2} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-xs resize-none" />
                    <div className="flex gap-2">
                      <button onClick={() => submitReply(post.id)} className="bg-gold text-black px-4 py-1.5 text-[10px] font-body font-medium hover:bg-gold-light transition-colors">Reply</button>
                      <button onClick={() => setReplyTo(null)} className="text-white/40 text-[10px] font-body hover:text-white">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button data-testid={`reply-btn-${post.id}`} onClick={() => setReplyTo(post.id)} className="text-gold/60 text-xs font-body hover:text-gold transition-colors flex items-center gap-1">
                    <MessageSquare size={12} /> Reply ({(post.replies || []).length})
                  </button>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
